/**
 * CameraHex — one camera rendered as a colored hexagon on the canvas.
 * Pure presentational + cheap (clipped divs, no video). Absolutely positioned at
 * (x,y) in canvas space; the parent transform handles pan/zoom.
 *
 * Glanceable layout (the full breakdown is in the hover popover / click card):
 *   • camera name (prominent, drop-shadowed for legibility on any fill)
 *   • alert count (number only) when alerting
 *   • two minimized status dots — Live and Rec (green = running, red = stopped)
 *
 * Alerting hexes follow the ss4/ss5 mock look: a SMALL flat severity core inside a
 * WIDE ring of the same colour, both blinking by OPACITY in OPPOSITE phase (ring
 * bright while the core is faded and vice versa — an alternating beacon), wrapped in
 * a soft hex-shaped glow. Tempo encodes urgency: critical fast, warning medium, info
 * slow.
 */
import type { CSSProperties } from 'react';
import { HEX_W, HEX_H, HEX_CLIP } from './hexLayout';
import { ALERT_FX, type HexColorState } from './types';
import type { HexCounter } from '../../api/contracts';

/** Hexagon path at scale `s` around the tile centre (s=1 → the full HEX_W×HEX_H hex). */
const hexPath = (s: number): string => {
  const cx = HEX_W / 2;
  const cy = HEX_H / 2;
  // Flat-top hexagon vertices (matches HEX_CLIP in hexLayout).
  const pts: [number, number][] = [
    [HEX_W * 0.25, 0],
    [HEX_W * 0.75, 0],
    [HEX_W, HEX_H * 0.5],
    [HEX_W * 0.75, HEX_H],
    [HEX_W * 0.25, HEX_H],
    [0, HEX_H * 0.5],
  ];
  return `M${pts.map(([x, y]) => `${cx + (x - cx) * s},${cy + (y - cy) * s}`).join('L')}Z`;
};
const HEX_PATH_OUTER = hexPath(1);
const HEX_PATH_CORE = hexPath(0.6);

// Smooth (ease-in-out) pulse keyframes. Injected once — a shared component can't rely
// on either app's Tailwind config carrying custom keyframes. The ring blinks by
// OPACITY (fading toward the canvas, never fully vanishing so the hex keeps its
// outline); the glow keyframes breathe a drop-shadow (which follows the clip-path,
// so the halo is hex-shaped).
// Versioned: bump when the keyframes CHANGE, or a stale style element with the old
// content would satisfy the guard and the new animation names would never register.
const ALERT_FX_KEYFRAMES_ID = 'hex-alert-fx-kf-v2';
if (typeof document !== 'undefined' && !document.getElementById(ALERT_FX_KEYFRAMES_ID)) {
  const el = document.createElement('style');
  el.id = ALERT_FX_KEYFRAMES_ID;
  el.textContent =
    '@keyframes hexRingPulse{0%,100%{opacity:.5}50%{opacity:1}}' +
    '@keyframes hexCorePulse{0%,100%{opacity:1}50%{opacity:.5}}' +
    '@keyframes hexGlowPulse{0%,100%{filter:drop-shadow(0 0 2px var(--hex-glow))}50%{filter:drop-shadow(0 0 14px var(--hex-glow))}}';
  document.head.appendChild(el);
}

interface StateStyle {
  /** Outer border colour (non-alerting states). */
  ring: string;
  /** Solid centre fill (non-alerting states). */
  fill: string;
  text: string;
  /** Alerting state → small centre + wide whitish blinking border. */
  blink: boolean;
}

const STYLES: Record<HexColorState, StateStyle> = {
  offline: { ring: 'bg-slate-600/50', fill: 'bg-slate-800', text: 'text-white', blink: false },
  grey: { ring: 'bg-slate-500/70', fill: 'bg-slate-600', text: 'text-white', blink: false },
  blue: { ring: 'bg-blue-800', fill: 'bg-blue-500', text: 'text-white', blink: true },
  amber: { ring: 'bg-amber-600', fill: 'bg-amber-400', text: 'text-white', blink: true },
  red: { ring: 'bg-red-800', fill: 'bg-red-500', text: 'text-white', blink: true },
};

interface CameraHexProps {
  name: string;
  colorState: HexColorState;
  /** Per-severity active-alert counts (drives the total count display). */
  counter?: HexCounter;
  /** Live stream running. */
  live: boolean;
  /** Recording running. */
  recording: boolean;
  x: number;
  y: number;
  selected?: boolean;
  /**
   * Filtered out by the toolbar. Drawn as a pale ghost rather than removed: the honeycomb
   * IS the operator's mental map of the site, and hexes that reflow every time a filter
   * changes destroy it. The ghost keeps the shape of the board intact and lets the matches
   * be the only coloured things on it — which is the whole point of filtering.
   */
  dimmed?: boolean;
  onClick?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}

/** A minimized status dot — green = running, red = stopped. The meaning of each
 *  dot (left = Live, right = Recording) is explained in the canvas legend. */
function StatusDot({ on, title }: { on: boolean; title: string }) {
  return (
    <span
      title={title}
      className={`h-2 w-2 rounded-full ring-1 ring-black/40 drop-shadow-[0_1px_1px_rgba(0,0,0,0.7)] ${
        on ? 'bg-emerald-400' : 'bg-red-500'
      }`}
    />
  );
}

export default function CameraHex({
  name,
  colorState,
  counter,
  live,
  recording,
  x,
  y,
  selected,
  dimmed = false,
  onClick,
  onHoverStart,
  onHoverEnd,
}: CameraHexProps) {
  const s = STYLES[colorState];
  // A ghost never blinks: a filtered-out hex that still pulsed would keep pulling the eye
  // to exactly what the operator asked to set aside.
  const alerting = s.blink && !selected && !dimmed;
  const fx = ALERT_FX[colorState];
  const ring = selected ? 'bg-brand' : dimmed ? 'bg-slate-400/40' : s.ring;
  const fill = dimmed ? 'bg-slate-400/25' : s.fill;
  // Border thickness of NON-alerting hexes (alerting ones draw their own SVG ring).
  const inset = selected ? 4 : 3;
  const total = counter && counter.total > 0 ? counter.total : null;

  return (
    <button
      type="button"
      data-hex="1"
      onClick={onClick}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      title={name}
      className={`absolute block focus:outline-none transition-transform duration-150 hover:z-10 hover:scale-[1.06] ${
        dimmed ? 'opacity-45 hover:opacity-100' : ''
      }`}
      style={{ left: x, top: y, width: HEX_W, height: HEX_H }}
    >
      {alerting && fx ? (
        <div
          className="absolute inset-0"
          style={
            {
              '--hex-glow': fx.glow,
              animation: `hexGlowPulse ${fx.duration} ease-in-out infinite`,
            } as CSSProperties
          }
        >
          {/* SVG so the ring is a true donut (outer hex MINUS the core hole). With
              stacked clipped divs the "ring" was a full hex behind the core, so the
              core's fade only revealed the same colour beneath and the centre never
              visibly changed. Here the canvas shows through whichever path is faded,
              so ring and core visibly ALTERNATE (ss4's pale-ring/solid-core at one
              end of the cycle, solid-ring/faint-core at the other). */}
          <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${HEX_W} ${HEX_H}`} aria-hidden>
            <path
              d={`${HEX_PATH_OUTER}${HEX_PATH_CORE}`}
              fillRule="evenodd"
              fill={fx.core}
              style={{ animation: `hexRingPulse ${fx.duration} ease-in-out infinite` }}
            />
            <path
              d={HEX_PATH_CORE}
              fill={fx.core}
              style={{ animation: `hexCorePulse ${fx.duration} ease-in-out infinite` }}
            />
          </svg>
        </div>
      ) : (
        <>
          <div className={`absolute inset-0 ${ring}`} style={{ clipPath: HEX_CLIP }} />
          <div
            className={`absolute ${fill}`}
            style={{ clipPath: HEX_CLIP, top: inset, left: inset, right: inset, bottom: inset }}
          />
        </>
      )}

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center">
        <span
          className={`text-[13px] font-bold leading-tight line-clamp-2 ${s.text} drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]`}
        >
          {name}
        </span>
        {total != null && (
          <span
            className={`rounded-full px-2 py-0.5 text-base font-extrabold leading-none tabular-nums shadow-sm ${
              alerting && fx ? 'bg-white' : 'bg-black/55 text-white'
            }`}
            style={alerting && fx ? { color: fx.core } : undefined}
          >
            {total}
          </span>
        )}
        <div className="flex items-center gap-2">
          <StatusDot on={live} title={live ? 'Live: running' : 'Live: stopped'} />
          <StatusDot on={recording} title={recording ? 'Recording: running' : 'Recording: stopped'} />
        </div>
      </div>
    </button>
  );
}
